import React, { Fragment } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { useQuery } from '@apollo/react-hooks';
import uniqBy from 'lodash/uniqBy';

import { token } from '@atlaskit/tokens';
import { N20, N40 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { HOME_OVERVIEW_RECENT_EXPERIENCE, ExperienceStop } from '@confluence/experience-tracker';
import { useSessionData } from '@confluence/session-data';
import { ErrorDisplay } from '@confluence/error-boundary';
import { START_TOUCH } from '@confluence/navdex';
import {
	PageSegmentLoadEnd,
	RECENT_WORK_ITEM_ON_HOME_PAGE_CLICK,
	setViewTransitionSource,
} from '@confluence/browser-metrics';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils';

import { useRightPanelVisible } from '../../RightPanel/useRightPanelVisible';
import { HOME_OVERVIEW_RECENT_PAGE_SEGMENT } from '../../../perf.config';
import { notEmpty, notDeleted } from '../homeViewsQueryHelpers';

import { RecentListItem } from './RecentListItem';
import { RecentWorkEmptyState } from './RecentWorkEmptyState';
import { HomeRecentWorkQuery } from './HomeRecentWorkQuery.graphql';
import type {
	HomeRecentWorkQuery as HomeRecentWorkQueryType,
	HomeRecentWorkQueryVariables,
} from './__types__/HomeRecentWorkQuery';
import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers';
import { ALL_RECENT_ITEMS_COUNT_HOME_REFRESH } from './recentListConstants';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const ListContainer = styled.div<{
	shouldShowRightPanel?: boolean;
}>`
	padding-top: ${token('space.150', '12px')};
	background-color: ${token('elevation.surface.raised', '#fff')};
	border-radius: ${token('border.radius.200', '8px')};
	border: 1px solid ${token('color.border', N40)};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GridContainer = styled.div<{ shouldShowRightPanel: boolean }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ shouldShowRightPanel }) => ({
		display: 'grid',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		gridTemplateColumns: !shouldShowRightPanel
			? 'repeat(calc(3 - var(--homeIsSingleColumn)), minmax(0, 1fr))'
			: 'repeat(2, minmax(0, 1fr))',
		gridTemplateRows: 'repeat(calc(2 + var(--homeIsSingleColumn), auto))',
		gap: token('space.200', '16px'),
	}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const TitleAndCreatorAndSpace = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex-shrink: 1;
	justify-content: space-between;
	min-width: 200px;
	margin-right: ${token('space.150', '12px')};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const ItemPlaceholder = styled.div`
	display: flex;
	height: 44px;
	padding: 10px ${token('space.250', '20px')};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const IconPlaceholder = styled.div`
	width: 24px;
	height: 24px;
	margin-top: ${token('space.025', '2px')};
	margin-right: 14px;
	background-color: ${token('color.skeleton', N20)};
	border-radius: 3px;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const TitlePlaceholder = styled.div`
	height: 18px;
	width: 60%;
	background-color: ${token('color.skeleton', N20)};
	border-radius: 3px;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const CreatorAndSpacePlaceholder = styled.div`
	height: 16px;
	width: 35%;
	background-color: ${token('color.skeleton', N20)};
	border-radius: 3px;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const LastActionPlaceholder = styled.div`
	height: 12px;
	width: 25%;
	background-color: ${token('color.skeleton', N20)};
	border-radius: 3px;
	margin-top: ${token('space.025', '2px')};
`;

export const filteredRecents = (
	data: HomeRecentWorkQueryType | undefined,
	isShowingMore: boolean,
	firstPageSize: number = ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
) => {
	const recents = uniqBy(
		(data?.activities?.myActivities?.all?.edges || [])
			.map((edge) => edge?.node)
			.filter(notEmpty)
			.filter((node) => notEmpty(node.object?.content))
			.filter((node) => notDeleted(node.object?.content)),
		(node) => node.object?.content?.id,
	);

	if (isShowingMore) {
		return recents;
	}

	return recents.slice(0, firstPageSize);
};

export const RecentList = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, isLoggedIn } = useSessionData();
	const { shouldShowRightPanel } = useRightPanelVisible();

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardViewedEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isSpaceAliasFFEnabled = true;
	const isDatabaseEnabled = isContentTypeEnabledInCurrentEnv('database');

	const { data, loading, error } = useQuery<HomeRecentWorkQueryType, HomeRecentWorkQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		HomeRecentWorkQuery,
		getRecentWorkQueryOptions({
			cloudId,
			isLoggedIn,
			isSpaceAliasFFEnabled,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			first: ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
		}),
	);

	const recents = filteredRecents(data, false, ALL_RECENT_ITEMS_COUNT_HOME_REFRESH);

	const onItemClick = (
		index: number,
		action: string,
		status: string | undefined,
		contentId: string,
		spaceKey: string,
		tenantId: string,
		objectType: string,
	) => {
		if (objectType === 'page') {
			setViewTransitionSource(RECENT_WORK_ITEM_ON_HOME_PAGE_CLICK);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'link',
				actionSubjectId: 'recentWorkItem',
				action: 'clicked',
				source: 'overviewScreen',
				attributes: {
					platformCtx: 'recentWork',
					destinationProduct: 'confluence',
					destinationTenantId: tenantId,
					destinationContainerType: 'space',
					destinationContainerId: spaceKey,
					destinationObjectType: objectType,
					destinationObjectId: contentId,
					pageLocation: 'home',
					ranking: index,
					navdexPointType: START_TOUCH,
					lastAction: action,
					contentStatus: status,
				},
			},
		}).fire();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'homeRecentWorkItem',
				action: 'clicked',
				source: 'overviewScreen',
				containerType: 'spaceKey',
				containerId: spaceKey,
				objectType,
				objectId: contentId,
				attributes: {
					ranking: index,
					navdexPointType: START_TOUCH,
					lastAction: action,
					contentStatus: status,
				},
			},
		}).fire();
	};

	const onItemSpaceClick = (index: number) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'homeRecentWorkItemSpace',
				action: 'clicked',
				source: 'overviewScreen',
				attributes: {
					ranking: index,
					navdexPointType: START_TOUCH,
				},
			},
		}).fire();
	};

	if (!loading && !recents.length) {
		return (
			<ListContainer>
				<RecentWorkEmptyState />
				{error && <ErrorDisplay error={error} />}
				<ExperienceStop name={HOME_OVERVIEW_RECENT_EXPERIENCE} error={error} />
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
			</ListContainer>
		);
	}

	return (
		<GridContainer shouldShowRightPanel={shouldShowRightPanel}>
			{loading && !recents.length
				? new Array(ALL_RECENT_ITEMS_COUNT_HOME_REFRESH).fill(0).map((_, i) => (
						<ItemPlaceholder key={i} data-testid="item-placeholder">
							<IconPlaceholder />
							<TitleAndCreatorAndSpace>
								<TitlePlaceholder />
								<CreatorAndSpacePlaceholder />
							</TitleAndCreatorAndSpace>
							<LastActionPlaceholder />
						</ItemPlaceholder>
					))
				: recents.map((recent, index) => (
						<RecentListItem
							key={recent.id}
							recent={recent}
							index={index}
							onItemClick={onItemClick}
							onItemSpaceClick={onItemSpaceClick}
							shouldAutofocus={index === ALL_RECENT_ITEMS_COUNT_HOME_REFRESH}
						/>
					))}
			{!loading && (
				<Fragment>
					{error && <ErrorDisplay error={error} />}
					<ExperienceStop name={HOME_OVERVIEW_RECENT_EXPERIENCE} error={error} />
					<PageSegmentLoadEnd metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
				</Fragment>
			)}
		</GridContainer>
	);
};
